import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { adminGetStripeConfig, adminUpdateStripeConfig, adminTestStripe, adminListPayments, adminGetAirwallexConfig, adminUpdateAirwallexConfig, adminTestAirwallex, adminGetPaymentProvider, adminSetPaymentProvider } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { CreditCard, Save, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff, ShieldCheck, Beaker, FlaskConical, ExternalLink, Info } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPayments() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecrets, setShowSecrets] = useState({ test_secret_key: false, live_secret_key: false, test_webhook_secret: false, live_webhook_secret: false, awx_api_key: false, awx_webhook_secret: false });
  const [transactions, setTransactions] = useState([]);
  // Payment provider selector + Airwallex config
  const [provider, setProvider] = useState('stripe');
  const [awx, setAwx] = useState(null);
  const [awxDraft, setAwxDraft] = useState({ enabled: false, mode: 'prod', client_id: '', api_key: '', webhook_secret: '' });
  const [awxSaving, setAwxSaving] = useState(false);
  const [awxTesting, setAwxTesting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadConfig();
    loadTransactions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, loading]);

  const loadConfig = async () => {
    try {
      const res = await adminGetStripeConfig();
      setCfg(res.data);
      setDraft({
        mode: res.data.mode || 'test',
        enabled: res.data.enabled || false,
        statement_descriptor: res.data.statement_descriptor || 'WINLUXOR',
        currency: res.data.currency || 'gbp',
        brand_name: res.data.brand_name || '',
        checkout_description: res.data.checkout_description || '',
        gateway_mode: res.data.gateway_mode || 'direct',
        external_api_url: res.data.external_api_url || '',
        external_api_key: '',
        external_webhook_secret: '',
        test_publishable_key: res.data.test_publishable_key || '',
        live_publishable_key: res.data.live_publishable_key || '',
        test_secret_key: '',
        test_webhook_secret: '',
        live_secret_key: '',
        live_webhook_secret: '',
      });
    } catch (e) {
      toast.error('Erreur de chargement');
    }
    try {
      const [pRes, aRes] = await Promise.all([adminGetPaymentProvider(), adminGetAirwallexConfig()]);
      setProvider(pRes.data?.active_provider || 'stripe');
      setAwx(aRes.data);
      setAwxDraft({
        enabled: aRes.data?.enabled || false,
        mode: aRes.data?.mode || 'prod',
        client_id: aRes.data?.client_id || '',
        api_key: '',
        webhook_secret: '',
      });
    } catch { /* airwallex config optional */ }
  };

  const switchProvider = async (p) => {
    if (p === provider) return;
    try {
      await adminSetPaymentProvider(p);
      setProvider(p);
      toast.success(p === 'airwallex' ? 'Airwallex est maintenant le prestataire actif' : 'Stripe est maintenant le prestataire actif');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const handleSaveAwx = async () => {
    setAwxSaving(true);
    try {
      const res = await adminUpdateAirwallexConfig(awxDraft);
      setAwx(res.data);
      setAwxDraft({ ...awxDraft, api_key: '', webhook_secret: '' });
      toast.success('Configuration Airwallex sauvegardée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setAwxSaving(false);
    }
  };

  const handleTestAwx = async () => {
    setAwxTesting(true);
    setTestResult(null);
    try {
      const res = await adminTestAirwallex();
      setTestResult({ ok: true, airwallex: true, ...res.data });
      toast.success('Connexion Airwallex valide ✓');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Échec de connexion Airwallex';
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setAwxTesting(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const res = await adminListPayments();
      setTransactions(res.data?.transactions || []);
    } catch {
      // ignore
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpdateStripeConfig(draft);
      toast.success('Configuration sauvegardée');
      await loadConfig();
    } catch (e) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminTestStripe();
      setTestResult({ ok: true, ...res.data });
      toast.success('Connexion Stripe valide ✓');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Échec de connexion';
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="min-h-screen flex bg-cream">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  const SecretInput = ({ field, label, placeholder, currentlySet }) => (
    <div className="space-y-2">
      <Label className="flex items-center justify-between">
        <span>{label}</span>
        {currentlySet && <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-300 text-[10px]">Configurée</Badge>}
      </Label>
      <div className="relative">
        <Input
          type={showSecrets[field] ? 'text' : 'password'}
          value={draft[field] || ''}
          onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
          placeholder={currentlySet ? '•••••••• (laissez vide pour ne pas changer)' : placeholder}
          className="pr-10 font-mono text-sm border-gold/30"
          data-testid={`stripe-${field.replace(/_/g, '-')}`}
        />
        <button
          type="button"
          onClick={() => setShowSecrets({ ...showSecrets, [field]: !showSecrets[field] })}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-rolex-green/55 hover:text-rolex-green"
          tabIndex={-1}
        >
          {showSecrets[field] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-cream">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-3xl font-bold text-rolex-green mb-1 flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-gold-dark" />
                Paiements
              </h1>
              <p className="text-rolex-green/65 text-sm">
                Choisissez votre prestataire (Stripe ou Airwallex) et configurez ses clés API — les paiements seront actifs immédiatement après sauvegarde.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {provider === 'stripe' ? (
                <>
                  <Badge className={cfg.enabled ? 'bg-emerald-500 text-white text-sm px-4 py-1.5' : 'bg-stone-300 text-stone-700 text-sm px-4 py-1.5'}>
                    {cfg.enabled ? '🟢 Stripe ACTIF' : '⚫ Stripe désactivé'}
                  </Badge>
                  <Badge className={cfg.mode === 'live' ? 'bg-red-600 text-white' : 'bg-blue-500 text-white'}>
                    {cfg.mode === 'live' ? '🔴 Mode LIVE' : '🧪 Mode TEST'}
                  </Badge>
                </>
              ) : (
                <>
                  <Badge className={awx?.enabled ? 'bg-emerald-500 text-white text-sm px-4 py-1.5' : 'bg-stone-300 text-stone-700 text-sm px-4 py-1.5'}>
                    {awx?.enabled ? '🟢 Airwallex ACTIF' : '⚫ Airwallex désactivé'}
                  </Badge>
                  <Badge className={awx?.mode === 'prod' ? 'bg-red-600 text-white' : 'bg-blue-500 text-white'}>
                    {awx?.mode === 'prod' ? '🔴 Mode LIVE' : '🧪 Mode SANDBOX'}
                  </Badge>
                </>
              )}
            </div>
          </div>

          {/* ── PROVIDER SELECTOR ── */}
          <Card className="mb-6 border-2 border-gold/40 shadow-lg bg-white">
            <CardHeader>
              <CardTitle className="text-rolex-green flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-gold-dark" />
                Prestataire de paiement actif
              </CardTitle>
              <p className="text-xs text-rolex-green/60 mt-1">Le client ne voit pas la différence — seul le prestataire qui traite la carte change.</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => switchProvider('stripe')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${provider === 'stripe' ? 'border-indigo-500 bg-indigo-50 shadow-md scale-[1.02]' : 'border-stone-200 hover:border-indigo-300'}`}
                  data-testid="provider-stripe-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="w-5 h-5 text-indigo-600" />
                    <span className="font-bold text-rolex-green">Stripe</span>
                    {provider === 'stripe' && <Badge className="bg-indigo-600 text-white text-[10px]">ACTIF</Badge>}
                  </div>
                  <p className="text-xs text-rolex-green/70">Checkout hébergé Stripe (direct ou passerelle externe).</p>
                </button>
                <button
                  type="button"
                  onClick={() => switchProvider('airwallex')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${provider === 'airwallex' ? 'border-orange-500 bg-orange-50 shadow-md scale-[1.02]' : 'border-stone-200 hover:border-orange-300'}`}
                  data-testid="provider-airwallex-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="w-5 h-5 text-orange-600" />
                    <span className="font-bold text-rolex-green">Airwallex</span>
                    {provider === 'airwallex' && <Badge className="bg-orange-600 text-white text-[10px]">ACTIF</Badge>}
                  </div>
                  <p className="text-xs text-rolex-green/70">Page de paiement hébergée Airwallex (cartes, 3DS).</p>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* ── AIRWALLEX CONFIG ── */}
          {provider === 'airwallex' && (
            <Card className="mb-6 border-2 border-orange-300 bg-orange-50/30 shadow-lg">
              <CardHeader className="bg-orange-100/50 border-b border-orange-200">
                <CardTitle className="text-rolex-green flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-orange-600" />
                  Configuration Airwallex
                </CardTitle>
                <p className="text-xs text-orange-800 mt-2">
                  Récupérez vos clés sur <strong>airwallex.com → Paramètres → Développeur → Clés API</strong>. ⚠️ Les abonnements VIP passent en paiement unique (pas de renouvellement automatique) avec Airwallex.
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setAwxDraft({ ...awxDraft, mode: 'demo' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${awxDraft.mode === 'demo' ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-stone-200 hover:border-blue-300'}`}
                    data-testid="awx-mode-demo-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Beaker className="w-5 h-5 text-blue-600" />
                      <span className="font-bold text-rolex-green">Mode SANDBOX</span>
                    </div>
                    <p className="text-xs text-rolex-green/70">Compte demo Airwallex. Carte test : 4012 0003 0000 0005.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAwxDraft({ ...awxDraft, mode: 'prod' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${awxDraft.mode === 'prod' ? 'border-red-500 bg-red-50 shadow-md' : 'border-stone-200 hover:border-red-300'}`}
                    data-testid="awx-mode-prod-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <ShieldCheck className="w-5 h-5 text-red-600" />
                      <span className="font-bold text-rolex-green">Mode LIVE</span>
                    </div>
                    <p className="text-xs text-rolex-green/70">Compte réel Airwallex. Vrais paiements.</p>
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-white border border-orange-200">
                  <div>
                    <Label className="text-rolex-green font-bold">Activer Airwallex sur le site</Label>
                    <p className="text-xs text-rolex-green/60 mt-1">Si désactivé, les boutons d'achat afficheront "Bientôt disponible".</p>
                  </div>
                  <Switch
                    checked={awxDraft.enabled}
                    onCheckedChange={(v) => setAwxDraft({ ...awxDraft, enabled: v })}
                    data-testid="awx-enabled-switch"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Client ID</span>
                    {awx?.client_id && <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-300 text-[10px]">Configuré</Badge>}
                  </Label>
                  <Input
                    value={awxDraft.client_id || ''}
                    onChange={(e) => setAwxDraft({ ...awxDraft, client_id: e.target.value })}
                    placeholder="ex: AbCdEf123..."
                    className="font-mono text-sm border-orange-300"
                    data-testid="awx-client-id"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Clé API (secret)</span>
                    {awx?.api_key_set && <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-300 text-[10px]">Configurée</Badge>}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showSecrets.awx_api_key ? 'text' : 'password'}
                      value={awxDraft.api_key || ''}
                      onChange={(e) => setAwxDraft({ ...awxDraft, api_key: e.target.value })}
                      placeholder={awx?.api_key_set ? '•••••••• (laissez vide pour ne pas changer)' : 'Clé API Airwallex'}
                      className="pr-10 font-mono text-sm border-orange-300"
                      data-testid="awx-api-key"
                    />
                    <button type="button" onClick={() => setShowSecrets({ ...showSecrets, awx_api_key: !showSecrets.awx_api_key })} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-rolex-green/55 hover:text-rolex-green" tabIndex={-1}>
                      {showSecrets.awx_api_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Secret du webhook</span>
                    {awx?.webhook_secret_set && <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-300 text-[10px]">Configuré</Badge>}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showSecrets.awx_webhook_secret ? 'text' : 'password'}
                      value={awxDraft.webhook_secret || ''}
                      onChange={(e) => setAwxDraft({ ...awxDraft, webhook_secret: e.target.value })}
                      placeholder={awx?.webhook_secret_set ? '•••••••• (laissez vide pour ne pas changer)' : 'Secret webhook Airwallex'}
                      className="pr-10 font-mono text-sm border-orange-300"
                      data-testid="awx-webhook-secret"
                    />
                    <button type="button" onClick={() => setShowSecrets({ ...showSecrets, awx_webhook_secret: !showSecrets.awx_webhook_secret })} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-rolex-green/55 hover:text-rolex-green" tabIndex={-1}>
                      {showSecrets.awx_webhook_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="bg-white border border-orange-200 rounded-lg p-4">
                  <p className="text-xs font-bold text-rolex-green mb-2">📡 URL de webhook à créer dans Airwallex (Développeur → Webhooks) :</p>
                  <code className="block bg-cream p-2 rounded font-mono text-xs break-all border border-orange-100">
                    {window.location.origin}/api/webhooks/airwallex
                  </code>
                  <p className="text-[11px] text-rolex-green/65 mt-2">
                    💡 Cochez au minimum l'événement <code className="bg-cream px-1 rounded">payment_intent.succeeded</code>, puis copiez le secret affiché dans le champ ci-dessus.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 items-center justify-between pt-2">
                  <Button
                    variant="outline"
                    onClick={handleTestAwx}
                    disabled={awxTesting || awxSaving}
                    className="border-orange-400 text-orange-700 hover:bg-orange-50"
                    data-testid="test-airwallex-btn"
                  >
                    {awxTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Beaker className="w-4 h-4" />}
                    Tester la connexion Airwallex
                  </Button>
                  <Button
                    onClick={handleSaveAwx}
                    disabled={awxSaving}
                    className="btn-gold gap-2 px-8"
                    data-testid="save-airwallex-config"
                  >
                    {awxSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-5 h-5" />}
                    Sauvegarder Airwallex
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {provider === 'stripe' && (<>
          {/* Mode + Activation */}
          <Card className="mb-6 border-2 border-gold/30 shadow-lg bg-white">
            <CardHeader>
              <CardTitle className="text-rolex-green flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-gold-dark" />
                Mode & Activation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">

              {/* Gateway selector */}
              <div className="space-y-3">
                <Label className="text-rolex-green font-bold">Mode de paiement</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, gateway_mode: 'direct' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${draft.gateway_mode === 'direct' ? 'border-gold bg-gold/10 shadow-md scale-[1.02]' : 'border-stone-200 hover:border-gold/40'}`}
                    data-testid="gateway-direct-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="w-5 h-5 text-gold-dark" />
                      <span className="font-bold text-rolex-green">Direct (Stripe)</span>
                    </div>
                    <p className="text-xs text-rolex-green/70">Utilise vos propres clés Stripe. Stripe voit ce site.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, gateway_mode: 'external' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${draft.gateway_mode === 'external' ? 'border-emerald-500 bg-emerald-50 shadow-md scale-[1.02]' : 'border-stone-200 hover:border-emerald-300'}`}
                    data-testid="gateway-external-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <ExternalLink className="w-5 h-5 text-emerald-600" />
                      <span className="font-bold text-rolex-green">Passerelle externe (Luxomarket)</span>
                    </div>
                    <p className="text-xs text-rolex-green/70">Délègue les paiements à un autre site. Stripe ne voit que Luxomarket.</p>
                  </button>
                </div>
              </div>

              {/* Test/Live mode (only relevant for direct gateway) */}
              {draft.gateway_mode === 'direct' && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gold/10">
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, mode: 'test' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${draft.mode === 'test' ? 'border-blue-500 bg-blue-50 shadow-md scale-[1.02]' : 'border-stone-200 hover:border-blue-300'}`}
                    data-testid="mode-test-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Beaker className="w-5 h-5 text-blue-600" />
                      <span className="font-bold text-rolex-green">Mode TEST</span>
                    </div>
                    <p className="text-xs text-rolex-green/70">Cartes de test (4242 4242 4242 4242). Aucun paiement réel.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, mode: 'live' })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${draft.mode === 'live' ? 'border-red-500 bg-red-50 shadow-md scale-[1.02]' : 'border-stone-200 hover:border-red-300'}`}
                    data-testid="mode-live-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <ShieldCheck className="w-5 h-5 text-red-600" />
                      <span className="font-bold text-rolex-green">Mode LIVE</span>
                    </div>
                    <p className="text-xs text-rolex-green/70">Vrais paiements. Les cartes de test ne fonctionneront PAS.</p>
                  </button>
                </div>
              )}

              <div className="flex items-center justify-between p-4 rounded-xl bg-cream border border-gold/20">
                <div>
                  <Label className="text-rolex-green font-bold">Activer Stripe sur le site</Label>
                  <p className="text-xs text-rolex-green/60 mt-1">Si désactivé, les boutons d'achat afficheront "Bientôt disponible". <strong>Indispensable dans les 2 modes.</strong></p>
                </div>
                <Switch
                  checked={draft.enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                  data-testid="stripe-enabled-switch"
                />
              </div>

              <div className="space-y-2">
                <Label>
                  Texte sur le relevé bancaire <span className="text-rolex-green/55 text-xs">(max 22 caractères)</span>
                </Label>
                <Input
                  value={draft.statement_descriptor || ''}
                  onChange={(e) => setDraft({ ...draft, statement_descriptor: e.target.value.slice(0, 22) })}
                  maxLength={22}
                  placeholder="LUXOMARKET PAY"
                  className="font-mono border-gold/30"
                  data-testid="statement-descriptor"
                />
                <p className="text-[11px] text-rolex-green/55">
                  💡 C'est ce que verra le client sur son <strong>relevé bancaire</strong>.
                  {draft.gateway_mode === 'external' && <span className="block mt-1 text-amber-700">⚠️ <strong>En mode "Passerelle externe"</strong> : ce texte est envoyé à Luxomarket. Luxomarket peut l'utiliser, ou utiliser son propre descriptor par défaut. Vérifiez avec l'agent Luxomarket.</span>}
                </p>
              </div>

              <div className="space-y-2">
                <Label>
                  🏷️ Nom de marque sur Stripe <span className="text-rolex-green/55 text-xs">(remplace le nom du produit)</span>
                </Label>
                <Input
                  value={draft.brand_name || ''}
                  onChange={(e) => setDraft({ ...draft, brand_name: e.target.value })}
                  placeholder="Luxomarket"
                  className="font-mono border-gold/30"
                  data-testid="brand-name"
                />
                <p className="text-[11px] text-rolex-green/55">
                  💡 Ce nom remplace "Pack 500 points" / "Rolex Submariner — 3 tickets" sur la page Stripe Checkout. <strong>Laissez vide</strong> pour afficher le vrai nom des produits.
                  {draft.gateway_mode === 'external' && <span className="block mt-1 text-emerald-700">✅ <strong>Fonctionne en mode externe</strong> — envoyé à Luxomarket et utilisé sur la page Stripe.</span>}
                </p>
              </div>

              <div className="space-y-2">
                <Label>
                  📝 Description sur Stripe Checkout <span className="text-rolex-green/55 text-xs">(remplace la description par défaut)</span>
                </Label>
                <Input
                  value={draft.checkout_description || ''}
                  onChange={(e) => setDraft({ ...draft, checkout_description: e.target.value })}
                  placeholder="Achat sécurisé"
                  className="border-gold/30"
                  data-testid="checkout-description"
                />
                <p className="text-[11px] text-rolex-green/55">
                  💡 Texte sous le nom du produit sur la page Stripe. Laissez vide pour la description automatique.
                  {draft.gateway_mode === 'external' && <span className="block mt-1 text-emerald-700">✅ <strong>Fonctionne en mode externe</strong> — envoyé à Luxomarket et affiché sur la page Stripe.</span>}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Devise</Label>
                <select
                  value={draft.currency || 'gbp'}
                  onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                  className="w-full p-2 border border-gold/30 rounded-md bg-white font-mono text-sm"
                  data-testid="currency-select"
                >
                  <option value="gbp">GBP (£) — British Pound</option>
                  <option value="eur">EUR (€) — Euro</option>
                  <option value="usd">USD ($) — US Dollar</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* External gateway config — only shown when gateway_mode === 'external' */}
          {draft.gateway_mode === 'external' && (
            <Card className="mb-6 border-2 border-emerald-300 bg-emerald-50/30 shadow-lg">
              <CardHeader className="bg-emerald-100/50 border-b border-emerald-200">
                <CardTitle className="text-rolex-green flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-emerald-600" />
                  Configuration Passerelle Luxomarket
                </CardTitle>
                <p className="text-xs text-emerald-800 mt-2">
                  Les paiements seront créés et traités par votre site Luxomarket. Stripe ne verra jamais Winluxor — il ne verra que Luxomarket comme marchand.
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="space-y-2">
                  <Label>URL de l'API Luxomarket</Label>
                  <Input
                    value={draft.external_api_url || ''}
                    onChange={(e) => setDraft({ ...draft, external_api_url: e.target.value })}
                    placeholder="https://luxomarket.fr"
                    className="font-mono border-emerald-300"
                    data-testid="external-api-url"
                  />
                  <p className="text-[11px] text-rolex-green/55">
                    💡 URL racine de Luxomarket (sans <code>/api</code>). Winluxor appellera <code>{(draft.external_api_url || 'https://luxomarket.fr')}/api/external/create-payment</code>.
                  </p>
                </div>
                <SecretInput field="external_api_key" label="Clé API Luxomarket (X-API-Key)" placeholder="lm_secret_xxxxx" currentlySet={cfg.external_api_key_set} />
                <SecretInput field="external_webhook_secret" label="Secret Webhook Luxomarket (HMAC)" placeholder="whk_xxxxx" currentlySet={cfg.external_webhook_set} />
                <div className="bg-white border border-emerald-200 rounded-lg p-4 mt-4">
                  <p className="text-xs font-bold text-rolex-green mb-2">📡 URL de callback à donner à Luxomarket :</p>
                  <code className="block bg-cream p-2 rounded font-mono text-xs break-all border border-emerald-100">
                    {window.location.origin}/api/payment/luxomarket-callback
                  </code>
                  <p className="text-[11px] text-rolex-green/65 mt-2">
                    💡 Luxomarket doit notifier cette URL après chaque paiement (paid / failed / cancelled). Voir le document <strong>"Spec Luxomarket.md"</strong> dans /app/.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* API Keys per mode (only relevant in direct gateway mode) */}
          {draft.gateway_mode === 'direct' && (
            <Tabs defaultValue={draft.mode || 'test'}>
            <TabsList className="grid grid-cols-2 max-w-md mb-4 bg-cream border border-gold/30">
              <TabsTrigger value="test" data-testid="keys-tab-test">🧪 Clés TEST</TabsTrigger>
              <TabsTrigger value="live" data-testid="keys-tab-live">🔴 Clés LIVE</TabsTrigger>
            </TabsList>

            <TabsContent value="test">
              <Card className="border-2 border-blue-200 bg-white shadow-lg">
                <CardHeader className="bg-blue-50/50">
                  <CardTitle className="text-blue-900 flex items-center gap-2">
                    <Beaker className="w-5 h-5" />
                    Clés API mode TEST
                  </CardTitle>
                  <p className="text-xs text-blue-700 mt-2">
                    Récupérez vos clés sur <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-semibold">dashboard.stripe.com/test/apikeys</a> (mode TEST activé en haut à droite)
                  </p>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <div className="space-y-2">
                    <Label>Clé publique de test</Label>
                    <Input
                      value={draft.test_publishable_key || ''}
                      onChange={(e) => setDraft({ ...draft, test_publishable_key: e.target.value })}
                      placeholder="pk_test_..."
                      className="font-mono text-sm border-gold/30"
                      data-testid="stripe-test-publishable-key"
                    />
                  </div>
                  <SecretInput field="test_secret_key" label="Clé secrète de test" placeholder="sk_test_..." currentlySet={cfg.test_secret_set} />
                  <SecretInput field="test_webhook_secret" label="Webhook secret de test (optionnel)" placeholder="whsec_..." currentlySet={cfg.test_webhook_set} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="live">
              <Card className="border-2 border-red-200 bg-white shadow-lg">
                <CardHeader className="bg-red-50/50">
                  <CardTitle className="text-red-900 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5" />
                    Clés API mode LIVE
                  </CardTitle>
                  <p className="text-xs text-red-700 mt-2">
                    Récupérez vos clés sur <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-semibold">dashboard.stripe.com/apikeys</a>. ⚠️ Ce sont de vrais paiements.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <div className="space-y-2">
                    <Label>Clé publique LIVE</Label>
                    <Input
                      value={draft.live_publishable_key || ''}
                      onChange={(e) => setDraft({ ...draft, live_publishable_key: e.target.value })}
                      placeholder="pk_live_..."
                      className="font-mono text-sm border-gold/30"
                      data-testid="stripe-live-publishable-key"
                    />
                  </div>
                  <SecretInput field="live_secret_key" label="Clé secrète LIVE" placeholder="sk_live_..." currentlySet={cfg.live_secret_set} />
                  <SecretInput field="live_webhook_secret" label="Webhook secret LIVE (optionnel)" placeholder="whsec_..." currentlySet={cfg.live_webhook_set} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          )}

          {/* Webhook info — only for direct mode */}
          {draft.gateway_mode === 'direct' && (
          <Card className="mt-6 border-2 border-gold/20 bg-cream">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-gold-dark shrink-0 mt-0.5" />
                <div className="text-sm text-rolex-green/80 space-y-2">
                  <p className="font-bold">📡 Configuration du Webhook Stripe</p>
                  <p>
                    Pour fiabiliser les paiements (au cas où le client ferme son navigateur), ajoutez ce endpoint dans Stripe :
                  </p>
                  <code className="block bg-white p-2 rounded border border-gold/20 font-mono text-xs break-all">
                    {window.location.origin}/api/webhook/stripe
                  </code>
                  <p>
                    👉 <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-gold-dark hover:underline font-semibold inline-flex items-center gap-1">Configurer le webhook <ExternalLink className="w-3 h-3" /></a>
                  </p>
                  <p className="text-xs text-rolex-green/55">
                    Sélectionnez les événements : <code className="bg-white px-1.5 py-0.5 rounded text-[10px]">checkout.session.completed</code>, <code className="bg-white px-1.5 py-0.5 rounded text-[10px]">invoice.paid</code>, <code className="bg-white px-1.5 py-0.5 rounded text-[10px]">customer.subscription.deleted</code>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Action buttons */}
          <div className="mt-6 flex flex-wrap gap-3 items-center justify-between">
            <div className="flex gap-3">
              {draft.gateway_mode === 'direct' && (
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={testing || saving}
                  className="border-gold/40 text-gold-dark hover:bg-gold/10"
                  data-testid="test-stripe-btn"
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Beaker className="w-4 h-4" />}
                  Tester la connexion
                </Button>
              )}
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn-gold gap-2 px-8 py-6 text-base"
              data-testid="save-stripe-config"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-5 h-5" />}
              Sauvegarder la configuration
            </Button>
          </div>
          </>)}

          {testResult && (
            <Card className={`mt-4 border-2 ${testResult.ok ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {testResult.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />}
                  <div className="text-sm">
                    {testResult.ok ? (
                      testResult.airwallex ? (
                        <>
                          <p className="font-bold text-emerald-900">✅ Connexion Airwallex OK</p>
                          <ul className="mt-2 space-y-0.5 text-emerald-800 text-xs">
                            <li>Mode : <strong>{testResult.mode === 'prod' ? 'LIVE' : 'SANDBOX'}</strong></li>
                            <li>API : <span className="font-mono">{testResult.base_url}</span></li>
                          </ul>
                        </>
                      ) : (
                      <>
                        <p className="font-bold text-emerald-900">✅ Connexion Stripe OK</p>
                        <ul className="mt-2 space-y-0.5 text-emerald-800 text-xs">
                          <li>Compte : <span className="font-mono">{testResult.account_id}</span></li>
                          <li>Mode : <strong>{testResult.mode?.toUpperCase()}</strong></li>
                          <li>Pays : {testResult.country}</li>
                          <li>Devise par défaut : {testResult.default_currency?.toUpperCase()}</li>
                          <li>Paiements actifs : {testResult.charges_enabled ? '✅' : '❌'}</li>
                          <li>Versements actifs : {testResult.payouts_enabled ? '✅' : '❌'}</li>
                        </ul>
                      </>
                      )
                    ) : (
                      <>
                        <p className="font-bold text-red-900">❌ Échec de la connexion</p>
                        <p className="text-red-800 mt-1 text-xs">{testResult.error}</p>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent transactions */}
          {transactions.length > 0 && (
            <Card className="mt-8 border-2 border-gold/20 bg-white">
              <CardHeader>
                <CardTitle className="text-rolex-green text-lg">Transactions récentes</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-cream border-b border-gold/20">
                      <tr>
                        <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-rolex-green/70">Date</th>
                        <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-rolex-green/70">Type</th>
                        <th className="text-right px-4 py-2 text-xs uppercase tracking-wider text-rolex-green/70">Montant</th>
                        <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-rolex-green/70">Statut</th>
                        <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-rolex-green/70">Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 25).map((t) => (
                        <tr key={t._id} className="border-b border-gold/10 hover:bg-cream/40">
                          <td className="px-4 py-2 text-xs text-rolex-green/65 font-mono">{new Date(t.created_at).toLocaleString()}</td>
                          <td className="px-4 py-2"><Badge variant="outline" className="text-xs">{t.type}</Badge></td>
                          <td className="px-4 py-2 text-right font-mono font-bold text-gold-dark">£{Number(t.amount_gbp || 0).toFixed(2)}</td>
                          <td className="px-4 py-2">
                            <Badge className={t.payment_status === 'paid' ? 'bg-emerald-500 text-white' : t.payment_status === 'unpaid' ? 'bg-amber-500 text-white' : 'bg-stone-300 text-stone-700'}>
                              {t.payment_status}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-xs text-rolex-green/65">{t.stripe_mode}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
